// ── lib/logEvent.js ──
//
// Fire-and-forget diagnostic event emitter. Calls the log-event Edge
// Function via the shared supabase client (functions.invoke).
//
// Rules:
//   - NEVER throws — a logging failure must be silent (console.warn only).
//   - NEVER feeds its own error back into logEvent (no infinite loop).
//   - NEVER forwards message / stack / componentStack (PII guard).
//   - Only the fields accepted by the app_events schema are forwarded.

import { supabase } from './supabase.js';

// Package version compiled in at build time by Vite.
// import.meta.env.VITE_APP_VERSION is not defined in this project, so we
// fall back to undefined — app_version will be omitted from the row.
// Do NOT invent a new build constant; wire it in vite.config.js if needed.
const APP_VERSION = import.meta.env.VITE_APP_VERSION || undefined;

/**
 * Log a diagnostic event. Fire-and-forget: always returns undefined.
 *
 * @param {object} payload
 * @param {'client_error'|'failed_op'} payload.kind     - Required.
 * @param {string} [payload.error_name]                  - e.g. error.name
 * @param {string} [payload.error_code]                  - classified code
 * @param {string} [payload.operation]                   - intakeStore action
 * @param {string} [payload.route]                       - current view slug
 * @param {number} [payload.status]                      - HTTP status if any
 * @param {string} [payload.session_id]                  - bare UUID
 */
export function logEvent(payload) {
  // Guard: if the Supabase client isn't available (test env, SSR), bail out.
  if (!supabase?.functions?.invoke) return;

  // Strip any free-text fields the caller might have included accidentally.
  // Destructure only the allowed schema columns; message/stack/componentStack
  // stay unbound here and are never forwarded.
  const {
    kind,
    error_name,
    error_code,
    operation,
    route,
    status,
    session_id,
  } = payload || {};

  const body = {
    kind,
    error_name:  error_name  || undefined,
    error_code:  error_code  || undefined,
    operation:   operation   || undefined,
    route:       route       || undefined,
    status:      status      !== undefined ? status : undefined,
    session_id:  session_id  || undefined,
    app_version: APP_VERSION,
  };

  // Invoke fire-and-forget. The Edge Function always returns 204.
  supabase.functions
    .invoke('log-event', { body })
    .catch(() => {
      // Swallow silently — a logging failure must never surface in the UI
      // or re-enter logEvent.
    });
}
